import type { MFE } from "@sinch/mfe-loader";
import type { ComponentType } from "react";
import type { DeepReadonly, Nominal } from "simplytyped";
import type { MmPaymentType } from "../../api/mm/mm.types";
import type { InboxVersion } from "../../hooks/useInboxConfig";
import type { TranslationKeys } from "../../intl/intl.types";
import type { RemoteName } from "../../mfe/mfeConfig";
import type { HiveConfig } from "../../portals/legacyEngage/hooks/useHiveConfig";
import type {
  Integration,
  IntegrationConnectionState,
} from "../../portals/messagemedia/hooks";

export type UniqueRouteKey = Nominal<string, "UniqueRouteKey">;

// TODO: split this type into portal specific types
export type IsRouteEnabledOptions = {
  hiveConfig?: Record<string, HiveConfig[number]["config_value"]> | undefined;
  integrations?: Integration[];
  integrationConnectionStates?: IntegrationConnectionState[];
  userRights?: string[];
  userCountry?: string;
  paymentType?: MmPaymentType | null;
  loadTemplateManagerMFE?: boolean | undefined;
  inboxVersion?: InboxVersion;
  routingParams?: Record<string, string | undefined>;
};

export type RouteConfigBase = {
  readonly key: UniqueRouteKey;
} & DeepReadonly<{
  /**  @deprecated - use mfe instead */
  import?: () => Promise<MFE>;
  /**
   * Information about the MFE loaded at this page - TODO: could be part of mfecat
   */
  mfe: MfeMetaData;
  /**
   * If provided, will be evaluated to decide if to show/render menu items and
   * routes. If no function is provided it will default to enabled.
   */
  isEnabled?: (params: IsRouteEnabledOptions) => boolean;
  /**
   * The path for which this route would be considered active.
   * Ending with a * would indicate a wildcard for all following path fragments.
   */
  path: string;
  /**
   * If a list of permissions are provided, only users having those permissions
   * will be allowed. Otherwise no limitation will be done.
   */
  allowedPermissions?: string[];
  /**
   * Hides the navigation UI
   */
  hideUi?: boolean;
  onProjectChange?: () => Promise<void>;
}>;

type ImportMfe =
  | {
      mfe: MfeMetaData;
    }
  | {
      mfe?: never;
    };

export type RouteConfigSubroute = Omit<RouteConfigBase, "mfe"> &
  ImportMfe &
  DeepReadonly<{
    hideUi?: never;
    Badge?: ComponentType<{ visible: boolean }>;
    href: string;
    textKey?: TranslationKeys;
  }>;

export type RouteConfigSubMenu = {
  textKey: TranslationKeys;
  subRoutes: RouteConfigSubroute[];
};

export type RouteConfigMenu = RouteConfigBase &
  DeepReadonly<{
    hideUi?: never;
    menu: {
      Badge?: ComponentType<{ visible: boolean }>;
      /**
       * The href that clicking the menu item will take you.
       */
      href: string;
      position: NavigationConfig[number]["position"];
      icon?: string;
      /**
       * A list of menu items that should be displayed within a collapsible menu
       * below this menu item.
       */
      subRouteSections?: RouteConfigSubMenu[];
      textKey: TranslationKeys;
      /**
       * Determine if their should be a divider after the menu element.
       */
      endSection?: boolean;
    };
  }>;

export type RouteConfig = Omit<RouteConfigMenu, "menu">;

type Position = "top" | "bottom";

export type MfeMetaData = {
  name: RemoteName;
  entry: string;
};

export type SubNavigationConfig = {
  sectionTitle: string;
  subRoutes: {
    label: string | undefined;
    href: string;
    path: string;
    mfe: MfeMetaData | undefined;
  }[];
};
export type NavigationConfig = readonly {
  href: string;
  icon?: string;
  key: string;
  mfe: MfeMetaData;
  path: string;
  position: Position;
  subPaths: string[];
  label: string;
  subNavigationConfig: SubNavigationConfig[] | undefined;
}[];

export const isRouteConfigMenu = (x: RouteConfig): x is RouteConfigMenu =>
  "menu" in x;
